import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { type FieldPath, type FieldPathValue, type FieldValues, type PathValue, useController } from 'react-hook-form';

type UseTransferListFieldArgs<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  name: TName;
};

export const useTransferListField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
}: UseTransferListFieldArgs<TFieldValues, TName>): {
  error: string | undefined;
  isSubmitting: boolean;
  items: PathValue<TFieldValues, TName>;
  setItems: Dispatch<SetStateAction<FieldPathValue<TFieldValues, TName>>>;
} => {
  const {
    formState: { isSubmitting },
    field: { onChange, value: items },
    fieldState: { error },
  } = useController<TFieldValues, TName>({ name });

  const setItems: Dispatch<SetStateAction<FieldPathValue<TFieldValues, TName>>> = useCallback(
    action => {
      // @ts-expect-error TypeScript cannot determine the type of items that will be used in SetStateAction because it
      // is inferred from FormData and RHF name
      const newValue = typeof action === 'function' ? action(items) : action;

      onChange(newValue);
    },
    [items, onChange]
  );

  return {
    error: error?.message,
    isSubmitting,
    items,
    setItems,
  };
};
